import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatCzk, formatDate, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { OrderTimeline } from "@/components/OrderTimeline";
import { StatusBadge } from "@/components/StatusBadge";
import { Upload, Star, Camera, ArrowLeft, ShieldAlert, Gavel, CreditCard, Clock, Loader2 } from "lucide-react";
import { resizeImage } from "@/lib/image";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_NEXT = {
  accepted: { next: "picked_up", label: "Označit jako vyzvednuté", role: "provider", needsPhoto: true },
  picked_up: { next: "processing", label: "Zahájit praní", role: "provider", needsPhoto: false },
  processing: { next: "delivered", label: "Označit jako doručené", role: "provider", needsPhoto: true },
  delivered: { next: "completed", label: "Potvrdit přijetí", role: "customer", needsPhoto: false },
};

const OrderDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [pollMsg, setPollMsg] = useState("");

  // review state
  const [review, setReview] = useState({ cleanliness: 5, communication: 5, speed: 5, overall: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Stripe return: poll status if session_id in URL
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPollMsg("");
      toast.error("Stav platby se nepodařilo ověřit. Zkuste obnovit stránku.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPollMsg("");
        toast.success("Platba úspěšná – objednávka je hrazená");
        // Clean URL and reload order
        searchParams.delete("session_id");
        setSearchParams(searchParams, { replace: true });
        await load();
        return;
      }
      if (data.status === "expired") {
        setPollMsg("");
        toast.error("Platební relace vypršela");
        return;
      }
      setPollMsg(`Ověřuji stav platby… (${attempts + 1}/${maxAttempts})`);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error("Polling error:", e);
      setPollMsg("");
      toast.error(formatErr(e));
    }
  }, [searchParams]);

  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      setPollMsg("Ověřuji stav platby…");
      pollPaymentStatus(sid);
    }
    if (searchParams.get("payment_cancelled")) {
      toast.info("Platba byla zrušena");
      searchParams.delete("payment_cancelled");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const startPayment = async () => {
    setPayBusy(true);
    try {
      const { data } = await api.post(`/payments/checkout/${id}`, {
        order_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e));
      setPayBusy(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await resizeImage(file));
    } catch (err) {
      toast.error(err.message || "Foto nelze nahrát");
    }
  };

  const updateStatus = async () => {
    const step = STATUS_NEXT[order.status];
    if (!step) return;
    if (step.needsPhoto && !photo) {
      toast.error("Pro tento krok je vyžadováno foto");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${id}/status`, { status: step.next, photo });
      toast.success("Status aktualizován");
      setPhoto("");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const acceptOrder = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/accept`);
      toast.success("Objednávka přijata");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!confirm("Opravdu zrušit objednávku?")) return;
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success("Objednávka zrušena");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      await api.post("/reviews", { order_id: id, ...review });
      toast.success("Děkujeme za hodnocení!");
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmittingReview(false); }
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="container-page py-20 text-center text-slate-500">Načítání…</div></div>);
  if (!order) return null;

  const isCustomer = user?.id === order.customer_id;
  const isProvider = user?.id === order.provider_id;
  const isAvailable = !order.provider_id && user?.role === "provider";
  const step = STATUS_NEXT[order.status];
  const canAdvance =
    step && ((step.role === "provider" && isProvider) || (step.role === "customer" && isCustomer));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10">
        <Link to={user?.role === "provider" ? "/dashboard/provider" : "/dashboard/customer"} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600">
          <ArrowLeft className="w-4 h-4" /> Zpět
        </Link>

        <div className="mt-4 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-7">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="tiny-label mb-2">Objednávka #{order.id.slice(0, 8)}</div>
                  <h1 className="font-display text-2xl font-bold text-slate-900">
                    {order.kg} kg · {order.service_type === "premium" ? "Premium" : "Standard"}
                    {order.express_type !== "none" && <span className="ml-2 text-amber-600 text-sm">({order.express_type === "24h" ? "Expres 24h" : "Same-day"})</span>}
                  </h1>
                  <div className="text-sm text-slate-500 mt-1">Vytvořeno {formatDate(order.created_at)}</div>
                </div>
                <StatusBadge status={order.status} className="text-sm" />
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <div>
                  <div className="tiny-label mb-2">Vyzvednutí</div>
                  <div className="text-slate-900 font-semibold">{order.pickup_address}</div>
                  <div className="text-sm text-slate-500">{order.pickup_city} {order.pickup_zip}</div>
                  <div className="text-sm text-slate-600 mt-1">Termín: {formatDate(order.pickup_time)}</div>
                  {(isCustomer || isProvider || user?.role === "admin") && (
                    <div className="text-sm text-slate-600">Tel: {order.pickup_phone}</div>
                  )}
                </div>
                <div>
                  <div className="tiny-label mb-2">Cena a podíl</div>
                  <div className="font-display text-2xl font-bold text-slate-900">{formatCzk(order.total_price)}</div>
                  <div className="text-xs text-slate-500">Provize 15 %: {formatCzk(order.commission)}</div>
                  <div className="text-xs text-slate-500">Prádelně: {formatCzk(order.provider_payout)}</div>
                </div>
              </div>

              {order.notes && (
                <div className="mt-5 p-3 rounded-lg bg-slate-50 text-sm text-slate-700">
                  <span className="font-semibold">Poznámka: </span>{order.notes}
                </div>
              )}

              {order.donation_amount > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold">
                  ♥ Dárce přidal {order.donation_amount} Kč pro lidi v nouzi
                </div>
              )}

              {order.preferences?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {order.preferences.map((p) => (
                    <span key={p} className="text-xs px-2 py-1 rounded-full bg-sky-100 text-sky-800">{p}</span>
                  ))}
                </div>
              )}
            </Card>

            {/* PAYMENT BANNER (customer-only, only after provider accepted) */}
            {isCustomer && order.status === "awaiting_payment" && order.payment_status !== "paid" && (
              <Card className="p-6 border-amber-300 bg-amber-50" data-testid="order-payment-banner">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-5 h-5 text-amber-700" />
                      <h3 className="font-display text-lg font-bold text-amber-900">
                        Prádelna {order.provider_name || ""} přijala zakázku – zaplaťte prosím
                      </h3>
                    </div>
                    <p className="text-sm text-amber-800">
                      Odkaz je platný 24 hodin, poté jej lze znovu vygenerovat. Pokud nezaplatíte do 48 h,
                      objednávka bude automaticky zrušena.
                    </p>
                    {pollMsg && (
                      <p className="text-xs text-sky-700 mt-2 inline-flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" /> {pollMsg}
                      </p>
                    )}
                  </div>
                  <Button onClick={startPayment} disabled={payBusy || !!pollMsg} className="bg-amber-600 hover:bg-amber-700" data-testid="order-pay-btn">
                    {payBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Zaplatit {formatCzk(order.grand_total || order.total_price)}
                  </Button>
                </div>
              </Card>
            )}

            {/* WAITING FOR PROVIDER (customer-only, before any laundry accepted) */}
            {isCustomer && order.status === "created" && order.payment_status !== "paid" && (
              <Card className="p-5 border-sky-300 bg-sky-50" data-testid="order-awaiting-provider-banner">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-sky-700 animate-spin" />
                  <div>
                    <h3 className="font-display text-base font-bold text-sky-900">Čekáme na prádelnu</h3>
                    <p className="text-sm text-sky-800">
                      Prádelnám v okolí jsme právě rozeslali upozornění. Jakmile někdo zakázku přijme,
                      pošleme vám e-mail s odkazem na platbu.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {isCustomer && order.payment_status === "paid" && (
              <Card className="p-4 border-emerald-300 bg-emerald-50 text-sm text-emerald-900 flex items-center gap-2" data-testid="order-paid-banner">
                <CreditCard className="w-4 h-4" />
                <strong>Zaplaceno</strong> · {order.paid_at && <span className="text-xs">{formatDate(order.paid_at)}</span>}
              </Card>
            )}

            {/* PROVIDER: informative banner while waiting for the customer to pay */}
            {isProvider && order.status === "awaiting_payment" && (
              <Card className="p-5 border-amber-300 bg-amber-50" data-testid="provider-awaiting-payment-banner">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-700" />
                  <div>
                    <h3 className="font-display text-base font-bold text-amber-900">Čekáme, až zákazník zaplatí</h3>
                    <p className="text-sm text-amber-800">
                      Zákazník obdržel e-mail s odkazem na platbu. Vyzvednout prádlo budete moci hned, jak platba dorazí.
                      Pokud nezaplatí do 48 h od vašeho přijetí, objednávka bude automaticky zrušena.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* ADMIN PAYMENT STATUS BANNER (always visible for admin) */}
            {user?.role === "admin" && (
              order.payment_status === "paid" ? (
                <Card className="p-4 border-emerald-300 bg-emerald-50 text-sm text-emerald-900 flex items-center gap-2" data-testid="admin-order-paid-banner">
                  <CreditCard className="w-4 h-4" />
                  <strong>Zákazník zaplatil</strong> · {order.paid_at && <span className="text-xs">{formatDate(order.paid_at)}</span>}
                  {order.paid_manually_by_admin_id && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 ml-1">Označeno ručně</span>}
                </Card>
              ) : (
                <Card className="p-5 border-amber-300 bg-amber-50" data-testid="admin-order-unpaid-banner">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display text-base font-bold text-amber-900 flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4" /> Zákazník zatím nezaplatil
                      </div>
                      <p className="text-xs text-amber-800">
                        Pokud Stripe Dashboard ukazuje úspěšnou platbu, ale tady je status <code className="px-1 rounded bg-amber-100">pending</code> (typicky kvůli nedoručenému webhooku), můžete platbu označit ručně.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-900 hover:bg-amber-100"
                      onClick={async () => {
                        if (!window.confirm("Opravdu označit jako zaplaceno? Pošle se notifikace zákazníkovi i prádelnám.")) return;
                        try {
                          await api.post(`/admin/orders/${order.id}/force-customer-paid`);
                          toast.success("Označeno jako zaplaceno");
                          await load();
                        } catch (err) { toast.error(formatErr(err)); }
                      }}
                      data-testid="admin-force-paid-btn"
                    >
                      Označit jako zaplaceno
                    </Button>
                  </div>
                </Card>
              )
            )}

            {/* Photos */}
            {(order.photo_pickup_bag || order.photo_picked_up || order.photo_delivered) && (
              <Card className="p-7">
                <div className="tiny-label mb-3">Fotodokumentace</div>
                <div className="grid grid-cols-3 gap-3">
                  {order.photo_pickup_bag && (
                    <div>
                      <img src={order.photo_pickup_bag} alt="Pytel" className="w-full h-32 object-cover rounded-lg border" />
                      <div className="text-xs text-slate-500 mt-1">Při zadání</div>
                    </div>
                  )}
                  {order.photo_picked_up && (
                    <div>
                      <img src={order.photo_picked_up} alt="Vyzvednutí" className="w-full h-32 object-cover rounded-lg border" />
                      <div className="text-xs text-slate-500 mt-1">Při vyzvednutí</div>
                    </div>
                  )}
                  {order.photo_delivered && (
                    <div>
                      <img src={order.photo_delivered} alt="Doručení" className="w-full h-32 object-cover rounded-lg border" />
                      <div className="text-xs text-slate-500 mt-1">Při doručení</div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Action panel */}
            {(canAdvance || isAvailable) && (
              <Card className="p-7 bg-sky-50/50 border-sky-200">
                <div className="tiny-label mb-3 text-sky-800">Další krok</div>
                {isAvailable && (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-slate-700">Tato objednávka je ve vašem městě a čeká na přijetí.</p>
                    <Button onClick={acceptOrder} disabled={busy} data-testid="order-accept">Přijmout</Button>
                  </div>
                )}
                {canAdvance && (
                  <>
                    {step.needsPhoto && (
                      <div className="mb-4">
                        <Label>Foto (povinné)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 hover:border-sky-400 text-sm">
                            <Upload className="w-4 h-4" /> Nahrát foto
                            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="status-photo" />
                          </label>
                          {photo && <img src={photo} alt="náhled" className="w-16 h-16 rounded-lg object-cover border" />}
                        </div>
                      </div>
                    )}
                    <Button onClick={updateStatus} disabled={busy} data-testid="order-advance">
                      {busy ? "Ukládám…" : step.label}
                    </Button>
                  </>
                )}
              </Card>
            )}

            {/* Review form */}
            {order.status === "completed" && isCustomer && !order.customer_reviewed && (
              <Card className="p-7">
                <div className="tiny-label mb-3">Ohodnoťte zakázku</div>
                <form onSubmit={submitReview} className="space-y-4">
                  {[
                    { k: "cleanliness", label: "Čistota" },
                    { k: "communication", label: "Komunikace" },
                    { k: "speed", label: "Rychlost" },
                    { k: "overall", label: "Celkové hodnocení" },
                  ].map((f) => (
                    <div key={f.k} className="flex items-center justify-between gap-4">
                      <span className="text-sm text-slate-700">{f.label}</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setReview({ ...review, [f.k]: n })} data-testid={`rating-${f.k}-${n}`}>
                            <Star className={`w-6 h-6 ${n <= review[f.k] ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <Label>Komentář (volitelné)</Label>
                    <Textarea rows={3} value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} data-testid="review-comment" />
                  </div>
                  <Button type="submit" disabled={submittingReview} data-testid="review-submit">
                    {submittingReview ? "Odesílám…" : "Odeslat hodnocení"}
                  </Button>
                </form>
              </Card>
            )}

            {/* Open dispute */}
            {(order.status === "delivered" || order.status === "completed") && isCustomer && !order.dispute_id && (
              <OpenDisputeCard orderId={order.id} onCreated={(did) => { setOrder({ ...order, dispute_id: did }); load(); }} />
            )}
            {order.dispute_id && (
              <Card className="p-5 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-3">
                  <Gavel className="w-5 h-5 text-amber-700" />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-900">K této objednávce byl otevřen spor</div>
                    <Link to={`/dispute/${order.dispute_id}`} className="text-sm text-amber-700 hover:underline" data-testid="order-open-dispute-link">Otevřít detail sporu →</Link>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar timeline */}
          <div className="space-y-6">
            <Card className="p-6">
              <div className="tiny-label mb-4">Stav objednávky</div>
              <OrderTimeline status={order.status} history={order.status_history || []} />
            </Card>

            {["created", "accepted"].includes(order.status) && (isCustomer || isProvider) && (
              <Card className="p-6 border-rose-200 bg-rose-50">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-rose-900">Potřebujete zrušit?</div>
                    <p className="text-sm text-rose-700 mt-1">Po vyzvednutí prádla už nelze.</p>
                    <Button variant="destructive" size="sm" className="mt-3" onClick={cancel} data-testid="order-cancel">Zrušit objednávku</Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderDetail;

const CATEGORIES = [
  { v: "damaged", l: "Poškozené prádlo" },
  { v: "missing_item", l: "Chybějící kus" },
  { v: "late_delivery", l: "Pozdní doručení" },
  { v: "quality", l: "Nespokojenost s kvalitou" },
  { v: "other", l: "Jiné" },
];

const OpenDisputeCard = ({ orderId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("damaged");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    for (const f of files) {
      try {
        const dataUrl = await resizeImage(f);
        setPhotos((arr) => [...arr, dataUrl]);
      } catch (err) {
        toast.error(err.message || `${f.name}: nelze nahrát`);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (description.length < 10) { toast.error("Popis musí mít alespoň 10 znaků"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/disputes", {
        order_id: orderId, category, description, photos,
      });
      toast.success("Spor odeslán k řešení");
      setOpen(false);
      onCreated?.(data.id);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-rose-50 border-rose-200">
      <div className="flex items-start gap-3">
        <Gavel className="w-5 h-5 text-rose-700 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-rose-900">Něco se pokazilo?</div>
          <p className="text-sm text-rose-700 mt-1">Otevřete spor a my pomůžeme najít řešení – plnou nebo částečnou náhradu.</p>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm" className="mt-3" data-testid="order-open-dispute">Otevřít spor</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nový spor</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Důvod</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="dispute-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Co se stalo? (min. 10 znaků)</Label>
                  <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="dispute-description" />
                </div>
                <div>
                  <Label>Fotografie (volitelné, max 3)</Label>
                  <input type="file" accept="image/*" multiple onChange={onFile} className="text-sm mt-2" data-testid="dispute-photos" />
                  {photos.length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {photos.map((p, i) => (
                        <img key={`${i}-${p.slice(-12)}`} src={p} alt={`fota ${i}`} className="w-16 h-16 rounded object-cover border" />
                      ))}
                    </div>
                  )}
                </div>
                <Button type="submit" disabled={busy} variant="destructive" className="w-full" data-testid="dispute-submit">
                  {busy ? "Odesílám…" : "Otevřít spor"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Card>
  );
};
